<script lang="ts">
	import Katex from './Katex.svelte';
	import uqnSplit from './functions/uqnSplit';
	import Complex from './functions/complexClass';
	import fractionBuilder from './functions/fractionBuilder';
	import brackets from './functions/brackets';
	import ComplexSpecial from './functions/complexSpecialClass';
	import signAssign from './functions/signAssign';

	export let uqn: number;
	export let qnNameCode = 0; // generate qn labels (a), (b), (c), etc

	let t: number;
	$: t = uqnToType(uqn);
	let realImag = 4;
	let qn: string;
	$: qn = uqnToQn(uqn,t);

	function uqnToType(u:number):number{
		if (u > 999999){
			return 5;	
		} else{
			return Number(uqn.toString()[0]);
		}
	}

	function uqnToQn(u:number,t:number):string{
		if (t==1){ // convert cartesian to exponential
			return type1Generator(u);
		} else if (t==2){ // convert exponential to cartesian
			return type2Generator(u);
		} else if (t==3 || t==4){ // complex special class;
			return type34Generator(u);
		} else if (t==5){ // multiply/divide/powers/conjugate
			return type5Generator(u);
		} else if (t==6) { // real/purely imaginary
			return type6Generator(u);
		} else{
			throw "type not valid";
		}
	}

	function type1Generator(u:number):string{
		let [_,a,b,sign] = uqnSplit(u);
		[a,b] = signAssign(sign,a,b);
		const z = new Complex(a,b);
		return z.toString();
	}

	function type2Generator(u:number):string{
		let [_, r,theta1,theta2,theta3] = uqnSplit(u);
		let theta = theta1 + theta2/10 + theta3/100;
		let thetaString = "";
		if (theta>3.14){
			theta = (6.28-theta);
			let thetaFixed = theta.toFixed(2);
			theta1 = Number(thetaFixed[0]);
			theta2 = Number(thetaFixed[2]);
			theta3 = Number(thetaFixed[3]);
			thetaString = "-";
		}
		if (theta3==0){
			if (theta2==0){
				thetaString += theta1.toString();
			} else{
				thetaString += theta1.toString() + "." + theta2.toString();
			}
		} else{
			thetaString += theta1.toString() + "." + theta2.toString() + theta3.toString();
		}		
		let rString = r.toString();
		if (r==1){
			rString = "";
		}
		return rString + "e^{" + thetaString + "i}";
	}

	function type34Generator(u:number):string{
		let r: number, n1:number, n2:number, type: number;
		[type, r, n1, n2] = uqnSplit(u);
		let n = n1*10 + n2;
		let z = new ComplexSpecial(n,r);
		if (type==3){
			return z.toString();
		} else{
			return z.toString(false);
		}
	}

	function type5Generator(u:number):string{
		let [power, n1, n2, n3, n4, n5, conj ] = uqnSplit(u);
		let n = n1*10000+n2*1000+n3*100+n4*10+n5;
		n1 = Math.floor(n/24/24);
		n2 = Math.floor((n-n1*24*24)/24);
		n3 = n%24;
		let r = 1;
		if (conj>4){
			conj = conj%5;
			r = 2;
		}
		let z1 = new ComplexSpecial(n1,r);
		let z2 = new ComplexSpecial(n2);
		let z3 = new ComplexSpecial(n3);
		switch(conj){
			case 0:
				return fractionBuilder(z1.toString(false)+brackets(z2.toString(false),"","\\left(","\\right)^*"),brackets(z3.toString(false),"","\\left(","\\right)^"+power.toString()),true);
			case 1:
				return fractionBuilder(z1.toString(false)+brackets(z2.toString(false),"","\\left(","\\right)^"+power.toString()),brackets(z3.toString(false),"","\\left(","\\right)^*"),true);
			case 2:
				return fractionBuilder(brackets(z1.toString(false)+brackets(z2.toString(false),"","\\left(","\\right)^"+power.toString()),"","\\left(","\\right)^*"),z3.toString(false),true);
			case 3:
				return brackets(fractionBuilder(brackets(z1.toString(false),"","\\left(","\\right)^"+power.toString())+z2.toString(false),z3.toString(false),true),"","\\left(","\\right)^*");
			case 4:
				return fractionBuilder(brackets(brackets(z1.toString(false),"","(",")^*")+z2.toString(false),"","\\left(","\\right)^"+power.toString()),z3.toString(false),true);
		}
	}

	function type6Generator(u:number):string{
		let n1:number, n2:number, _;
		[_, n1, n2, realImag] = uqnSplit(u);
		let n = n1*10+n2;
		let z = new ComplexSpecial(n);
		return brackets(z.toString(false),"","\\left(","\\right)^n");
	}
	
</script>

<p> 
	({String.fromCharCode(qnNameCode+97)})
	{#if t<5}
		Convert
	{:else if t==5}
		Evaluate
	{:else if t==6}
		Find the smallest postive integer value of <Katex math="n"/> such that
	{/if}
	<Katex math={qn}/>{#if t==5}.{/if}
	{#if t<5}
		to
		{#if t%2==1}
			Euler/exponential form <Katex math={"re^{i\\theta}"}/>.
		{:else}
			cartesian form <Katex math="x+yi"/>.
		{/if}
	{:else if t==6}
		is
		{#if realImag==0}
			real.
		{:else if realImag==1}
			real and positive.
		{:else if realImag==2}
			real and negative.
		{:else if realImag==3}
			purely imaginary.
		{/if}
	{/if}
</p>